import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { LogOut, Users, UserCheck, UserX, Search, Eye, CheckCircle, XCircle, Clock, KeyRound, Copy, AlertCircle, Shield, Settings, FileText, Database, Trash2, Palette, MessageSquare, Mail, ChevronLeft, ChevronRight } from 'lucide-react';
import { formatPhoneDisplay } from '@/utils/phoneUtils';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const [brokers, setBrokers] = useState([]);
  const [agents, setAgents] = useState([]);
  const [resetRequests, setResetRequests] = useState([]);
  const [serviceProviders, setServiceProviders] = useState([]);
  const [filteredBrokers, setFilteredBrokers] = useState([]);
  const [filteredAgents, setFilteredAgents] = useState([]);
  const [filteredProviders, setFilteredProviders] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [agentSearchQuery, setAgentSearchQuery] = useState('');
  const [providerSearchQuery, setProviderSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [resetPasswordData, setResetPasswordData] = useState(null);
  const [activeTab, setActiveTab] = useState('brokers'); // 'brokers', 'agents', 'reset-requests', 'service-providers'
  const [viewProviderModalOpen, setViewProviderModalOpen] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletePasscode, setDeletePasscode] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [editEmailProvider, setEditEmailProvider] = useState(null);
  const [newEmail, setNewEmail] = useState('');
  const [emailSaving, setEmailSaving] = useState(false);

  // Pagination state
  const [brokerPage, setBrokerPage] = useState(1);
  const [brokerPageSize, setBrokerPageSize] = useState(20);
  const [agentPage, setAgentPage] = useState(1);
  const [agentPageSize, setAgentPageSize] = useState(20);
  const [providerPage, setProviderPage] = useState(1);
  const [providerPageSize, setProviderPageSize] = useState(20);

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchData();
  }, []);

  useEffect(() => {
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      const filtered = brokers.filter(broker => 
        (broker.full_name && broker.full_name.toLowerCase().includes(query)) ||
        broker.email.toLowerCase().includes(query) ||
        broker.id.toLowerCase().includes(query)
      );
      setFilteredBrokers(filtered);
    } else {
      setFilteredBrokers(brokers);
    }
  }, [searchQuery, brokers]);

  useEffect(() => {
    setBrokerPage(1);
  }, [searchQuery]);

  useEffect(() => {
    if (agentSearchQuery.trim()) {
      const query = agentSearchQuery.toLowerCase();
      const filtered = agents.filter(agent => 
        agent.name.toLowerCase().includes(query) ||
        agent.email.toLowerCase().includes(query) ||
        agent.id.toLowerCase().includes(query) ||
        (agent.broker_name && agent.broker_name.toLowerCase().includes(query))
      );
      setFilteredAgents(filtered);
    } else {
      setFilteredAgents(agents);
    }
  }, [agentSearchQuery, agents]);

  useEffect(() => {
    setAgentPage(1);
  }, [agentSearchQuery]);

  useEffect(() => {
    if (providerSearchQuery.trim()) {
      const query = providerSearchQuery.toLowerCase();
      const filtered = serviceProviders.filter(provider => 
        provider.full_name?.toLowerCase().includes(query) ||
        provider.company_name?.toLowerCase().includes(query) ||
        provider.email?.toLowerCase().includes(query) ||
        provider.phone?.toLowerCase().includes(query) ||
        provider.service_type?.toLowerCase().includes(query) ||
        provider.city?.toLowerCase().includes(query)
      );
      setFilteredProviders(filtered);
    } else {
      setFilteredProviders(serviceProviders);
    }
  }, [providerSearchQuery, serviceProviders]);

  useEffect(() => {
    setProviderPage(1);
  }, [providerSearchQuery]);

  const fetchData = async () => {
    const token = localStorage.getItem('adminToken');
    try {
      const [statsRes, brokersRes, agentsRes, resetRequestsRes, providersRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/brokers`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/agents`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/password-reset-requests`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/service-providers`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setStats(statsRes.data);
      setBrokers(brokersRes.data);
      setAgents(agentsRes.data.agents || []);  // Extract agents array
      setResetRequests(resetRequestsRes.data.requests || []);  // Extract requests array
      setServiceProviders(providersRes.data.providers || []);  // Extract providers array
      setFilteredBrokers(brokersRes.data);
      setFilteredAgents(agentsRes.data.agents || []);  // Extract agents array
      setFilteredProviders(providersRes.data.providers || []);  // Extract providers array
    } catch (error) {
      toast.error('Failed to fetch data');
      if (error.response?.status === 403) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (brokerId) => {
    setActionLoading(brokerId);
    const token = localStorage.getItem('adminToken');
    try {
      await axios.patch(`${API}/admin/brokers/${brokerId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Broker approved successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to approve broker');
    } finally {
      setActionLoading(null);
    }
  };

  const handleToggleStatus = async (brokerId, currentStatus) => {
    setActionLoading(brokerId);
    const token = localStorage.getItem('adminToken');
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    
    try {
      await axios.patch(`${API}/admin/brokers/${brokerId}/status`, 
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success(`Broker ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setActionLoading(null);
    }
  };

  const handleTogglePremium = async (brokerId, currentPremium) => {
    const token = localStorage.getItem('adminToken');
    try {
      const response = await axios.patch(`${API}/admin/brokers/${brokerId}/premium`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const newPremium = response.data.premium;
      toast.success(`Broker ${newPremium ? 'set as Premium' : 'removed from Premium'}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update premium status');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminData');
    navigate('/login');
  };

  const handleResetPassword = async (userId, userName) => {
    if (!window.confirm(`Reset password for ${userName}? A new random password will be generated.`)) {
      return;
    }

    setActionLoading(userId);
    const token = localStorage.getItem('adminToken');
    
    try {
      const response = await axios.post(
        `${API}/admin/users/${userId}/reset-password`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setResetPasswordData({
        userName,
        newPassword: response.data.new_password
      });
      setShowPasswordDialog(true);
      toast.success('Password reset successfully!');
    } catch (error) {
      toast.error('Failed to reset password');
    } finally {
      setActionLoading(null);
    }
  };

  const copyPasswordToClipboard = () => {
    if (resetPasswordData?.newPassword) {
      navigator.clipboard.writeText(resetPasswordData.newPassword);
      toast.success('Password copied to clipboard!');
    }
  };

  const handleDeleteProvider = async (providerId) => {
    if (!window.confirm('Are you sure you want to delete this service provider?')) {
      return;
    }
    
    setActionLoading(providerId);
    const token = localStorage.getItem('adminToken');
    try {
      await axios.delete(`${API}/admin/service-providers/${providerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Service provider deleted successfully');
      fetchData(); // Refresh data
    } catch (error) {
      toast.error('Failed to delete service provider');
      console.error(error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleResetProviderPassword = async (providerId, providerName) => {
    if (!window.confirm(`Reset password for ${providerName}?`)) {
      return;
    }

    setActionLoading(providerId);
    const token = localStorage.getItem('adminToken');
    try {
      const response = await axios.post(
        `${API}/admin/service-providers/${providerId}/reset-password`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResetPasswordData({
        userName: providerName,
        newPassword: response.data.new_password
      });
      setShowPasswordDialog(true);
      toast.success('Password reset successfully!');
    } catch (error) {
      toast.error('Failed to reset password');
      console.error(error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleToggleProviderStatus = async (providerId, currentStatus, providerName) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    
    setActionLoading(providerId);
    const token = localStorage.getItem('adminToken');
    try {
      await axios.patch(
        `${API}/admin/service-providers/${providerId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${providerName} set to ${newStatus}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update provider status');
      console.error(error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleUpdateProviderEmail = async () => {
    if (!newEmail || !newEmail.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }
    setEmailSaving(true);
    const token = localStorage.getItem('adminToken');
    try {
      await axios.patch(
        `${API}/admin/service-providers/${editEmailProvider.id}/email`,
        { email: newEmail },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Email updated to ${newEmail}`);
      setEditEmailProvider(null);
      setNewEmail('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update email');
    } finally {
      setEmailSaving(false);
    }
  };

  const handleResetAndFulfill = async (requestId, userId, userName, userType) => {
    if (!window.confirm(`Reset password for ${userName}? A new random password will be generated.`)) {
      return;
    }

    setActionLoading(userId);
    const token = localStorage.getItem('adminToken');

    try {
      // Use the fulfill endpoint which resets the password AND marks request as fulfilled
      const response = await axios.post(
        `${API}/admin/password-reset-requests/${requestId}/fulfill`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setResetPasswordData({
        userName,
        newPassword: response.data.new_password
      });
      setShowPasswordDialog(true);
      toast.success('Password reset and request fulfilled!');
      fetchData();
    } catch (error) {
      console.error('Failed to reset and fulfill:', error);
      toast.error('Failed to reset password');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteResetRequest = async (requestId, userName) => {
    if (!window.confirm(`Are you sure you want to delete the reset request for ${userName}?`)) {
      return;
    }

    try {
      setActionLoading(requestId);
      const token = localStorage.getItem('adminToken');
      await axios.delete(
        `${API}/admin/password-reset-requests/${requestId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Reset request deleted successfully');
      fetchData();
    } catch (error) {
      console.error('Failed to delete reset request:', error);
      toast.error('Failed to delete reset request');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteWithPasscode = async () => {
    if (!deletePasscode || deletePasscode.length !== 6) {
      toast.error('Please enter your 6-digit admin passcode');
      return;
    }
    setDeleteLoading(true);
    const token = localStorage.getItem('adminToken');
    const endpoint = deleteTarget.type === 'broker'
      ? `${API}/admin/brokers/${deleteTarget.id}`
      : `${API}/admin/agents/${deleteTarget.id}`;
    try {
      await axios.delete(endpoint, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        data: { passcode: deletePasscode }
      });
      toast.success(`${deleteTarget.type === 'broker' ? 'Broker' : 'Agent'} "${deleteTarget.name}" deleted successfully`);
      setDeleteTarget(null);
      setDeletePasscode('');
      fetchData();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Delete failed';
      toast.error(msg);
    } finally {
      setDeleteLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      active: 'bg-green-100 text-green-800 border-green-300',
      inactive: 'bg-red-100 text-red-800 border-red-300'
    };
    const icons = {
      pending: <Clock size={14} />,
      active: <CheckCircle size={14} />,
      inactive: <XCircle size={14} />
    };
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold border ${styles[status]}`}>
        {icons[status]}
        {status.toUpperCase()}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F5F0]">
        <p className="text-stone-500">Loading...</p>
      </div>
    );
  }

  // Pagination helper
  const PaginationBar = ({ filtered, page, setPage, pageSize, setPageSize, label }) => {
    const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
    return (
      <div className="px-4 md:px-6 py-3 border-t border-stone-200 flex flex-col sm:flex-row items-center justify-between gap-2 bg-stone-50">
        <p className="text-sm text-stone-500">
          Showing {Math.min((page - 1) * pageSize + 1, filtered.length)}-{Math.min(page * pageSize, filtered.length)} of {filtered.length} {label}
        </p>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-stone-500">Per page:</span>
            <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setPage(1); }}>
              <SelectTrigger className="w-[70px] h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="20">20</SelectItem>
                <SelectItem value="30">30</SelectItem>
                <SelectItem value="50">50</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" className="h-8 w-8 p-0" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
              <ChevronLeft size={16} />
            </Button>
            <span className="text-xs font-medium text-stone-700 px-2">{page}/{totalPages}</span>
            <Button variant="outline" size="sm" className="h-8 w-8 p-0" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
              <ChevronRight size={16} />
            </Button>
          </div>
        </div>
      </div>
    );
  };

  // Paginate helper
  const paginate = (arr, page, pageSize) => arr.slice((page - 1) * pageSize, page * pageSize);

  return (
    <div className="min-h-screen bg-[#F5F5F0]">
      {/* Header */}
      <div className="bg-white border-b border-stone-200 px-4 md:px-6 py-3 md:py-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <h1 className="text-xl md:text-3xl font-bold text-[#1A4D2E]" style={{fontFamily: '"Playfair Display", serif'}}>
            Admin Dashboard
          </h1>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => navigate('/admin/contact-requests')}
              variant="outline"
              className="text-stone-600 hover:text-[#1A4D2E] text-sm md:text-base border-stone-300"
              size="sm"
              data-testid="contact-requests-btn"
            >
              <MessageSquare size={18} className="mr-0 md:mr-2" />
              <span className="hidden md:inline">Contact Requests</span>
            </Button>
            <Button
              onClick={() => navigate('/admin/master-data')}
              variant="outline"
              className="text-stone-600 hover:text-[#1A4D2E] text-sm md:text-base border-stone-300"
              size="sm"
            >
              <Database size={18} className="mr-0 md:mr-2" />
              <span className="hidden md:inline">Master Data Management</span>
            </Button>
            <Button
              onClick={() => navigate('/admin/connect')}
              variant="outline"
              className="text-stone-600 hover:text-[#1A4D2E] text-sm md:text-base border-stone-300"
              size="sm"
              data-testid="admin-connect-link"
            >
              <MessageSquare size={18} className="mr-0 md:mr-2" />
              <span className="hidden md:inline">Connect Moderation</span>
            </Button>
            <Button
              onClick={() => navigate('/admin/logs')}
              variant="outline"
              className="text-stone-600 hover:text-[#1A4D2E] text-sm md:text-base border-stone-300"
              size="sm"
            >
              <FileText size={18} className="mr-0 md:mr-2" />
              <span className="hidden md:inline">Logs</span>
            </Button>
            <Button
              onClick={() => navigate('/admin/settings')}
              variant="outline"
              className="text-stone-600 hover:text-[#1A4D2E] text-sm md:text-base border-stone-300"
              size="sm"
            >
              <Settings size={18} className="mr-0 md:mr-2" />
              <span className="hidden md:inline">Settings</span>
            </Button>
            <Button
              onClick={() => navigate('/admin/site-settings')}
              variant="outline"
              className="text-stone-600 hover:text-[#1A4D2E] text-sm md:text-base border-stone-300"
              size="sm"
              data-testid="site-settings-link"
            >
              <Palette size={18} className="mr-0 md:mr-2" />
              <span className="hidden md:inline">Site Settings</span>
            </Button>
            <Button
              onClick={handleLogout}
              variant="ghost"
              className="text-stone-600 hover:text-red-600 text-sm md:text-base"
              size="sm"
            >
              <LogOut size={18} className="mr-0 md:mr-2" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-4 md:p-6 space-y-4 md:space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="p-6 bg-white">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Users className="text-blue-600" size={24} />
              </div>
              <div>
                <p className="text-sm text-stone-500">Total Brokers</p>
                <p className="text-2xl font-bold text-stone-800">{stats.total_brokers || 0}</p>
              </div>
            </div>
          </Card>

          <Card className="p-6 bg-white">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-yellow-100 rounded-lg">
                <Clock className="text-yellow-600" size={24} />
              </div>
              <div>
                <p className="text-sm text-stone-500">Pending Approval</p>
                <p className="text-2xl font-bold text-stone-800">{stats.pending_brokers || 0}</p>
              </div>
            </div>
          </Card>

          <Card className="p-6 bg-white">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-lg">
                <UserCheck className="text-green-600" size={24} />
              </div>
              <div>
                <p className="text-sm text-stone-500">Active Brokers</p>
                <p className="text-2xl font-bold text-stone-800">{stats.active_brokers || 0}</p>
              </div>
            </div>
          </Card>

          <Card className="p-6 bg-white">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-100 rounded-lg">
                <Users className="text-purple-600" size={24} />
              </div>
              <div>
                <p className="text-sm text-stone-500">Total Agents</p>
                <p className="text-2xl font-bold text-stone-800">{stats.total_agents || 0}</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Tabbed Interface for Management Sections */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-4 mb-6">
            <TabsTrigger value="brokers" className="flex items-center gap-2">
              <Users size={18} />
              Brokers
            </TabsTrigger>
            <TabsTrigger value="agents" className="flex items-center gap-2">
              <Users size={18} />
              Agents
            </TabsTrigger>
            <TabsTrigger value="service-providers" className="flex items-center gap-2">
              <Shield size={18} />
              Providers
            </TabsTrigger>
            <TabsTrigger value="reset-requests" className="flex items-center gap-2">
              <Shield size={18} />
              Reset
            </TabsTrigger>
          </TabsList>

          {/* Brokers Tab */}
          <TabsContent value="brokers" className="space-y-6">
            {/* Search Bar */}
            <Card className="p-4 bg-white">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" size={20} />
                <Input
                  placeholder="Search by name, email, or broker ID..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 h-12 border-stone-300"
                />
              </div>
            </Card>

            {/* Brokers Table */}
        <Card className="bg-white overflow-hidden">
          <div className="px-4 md:px-6 py-4 border-b border-stone-200">
            <h2 className="text-lg md:text-xl font-bold text-stone-800">Broker Management</h2>
          </div>
          
          {/* Mobile Card View */}
          <div className="md:hidden divide-y divide-stone-200">
            {filteredBrokers.length === 0 ? (
              <div className="px-6 py-8 text-center text-stone-500">
                No brokers found
              </div>
            ) : (
              paginate(filteredBrokers, brokerPage, brokerPageSize).map((broker) => (
                <div key={broker.id} className="p-4 hover:bg-stone-50">
                  <div className="space-y-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-semibold text-stone-800">{broker.full_name}</p>
                        <p className="text-xs text-stone-500 mt-1">ID: {broker.id.slice(0, 8)}...</p>
                      </div>
                      {getStatusBadge(broker.status)}
                    </div>
                    
                    <div className="space-y-1 text-sm">
                      <p className="text-stone-600">{broker.email}</p>
                      <div className="flex items-center gap-1 text-stone-500">
                        <Users size={14} />
                        <span>{broker.agent_count} agents</span>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2 pt-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate(`/admin/brokers/${broker.id}`)}
                        className="flex-1"
                      >
                        <Eye size={16} className="mr-1" />
                        View
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
          
          {/* Desktop Grid View */}
          <div className="hidden md:block overflow-x-auto">
            <div className="w-full">
              <div className="grid grid-cols-[1.5fr_2fr_0.8fr_1fr_1fr] bg-stone-50 border-b border-stone-200">
                <div className="px-6 py-3 text-left text-xs font-semibold text-stone-600 uppercase">Broker</div>
                <div className="px-6 py-3 text-left text-xs font-semibold text-stone-600 uppercase">Email</div>
                <div className="px-6 py-3 text-left text-xs font-semibold text-stone-600 uppercase">Agents</div>
                <div className="px-6 py-3 text-left text-xs font-semibold text-stone-600 uppercase">Status</div>
                <div className="px-6 py-3 text-right text-xs font-semibold text-stone-600 uppercase">Actions</div>
              </div>
              <div className="divide-y divide-stone-200">
                {filteredBrokers.length === 0 ? (
                  <div className="px-6 py-8 text-center text-stone-500">
                    No brokers found
                  </div>
                ) : (
                  paginate(filteredBrokers, brokerPage, brokerPageSize).map((broker) => (
                    <div key={broker.id} className="grid grid-cols-[1.5fr_2fr_0.8fr_1fr_1fr] items-center hover:bg-stone-50">
                      <div className="px-6 py-4">
                        <p className="font-semibold text-stone-800">{broker.full_name}</p>
                        <p className="text-xs text-stone-500">ID: {broker.id.slice(0, 8)}...</p>
                      </div>
                      <div className="px-6 py-4 text-stone-600">{broker.email}</div>
                      <div className="px-6 py-4">
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-stone-100 rounded-full text-sm">
                          <Users size={14} />
                          {broker.agent_count}
                        </span>
                      </div>
                      <div className="px-6 py-4">{getStatusBadge(broker.status)}</div>
                      <div className="px-6 py-4">
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => navigate(`/admin/brokers/${broker.id}`)}
                          >
                            <Eye size={16} className="mr-1" />
                            View
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </Card>
            {filteredBrokers.length > 0 && (
              <PaginationBar filtered={filteredBrokers} page={brokerPage} setPage={setBrokerPage} pageSize={brokerPageSize} setPageSize={setBrokerPageSize} label="brokers" />
            )}
          </TabsContent>
          <TabsContent value="agents" className="space-y-6">
            {/* Agent Management Table */}
        <Card className="bg-white overflow-hidden">
          <div className="px-4 md:px-6 py-4 border-b border-stone-200">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <h2 className="text-lg md:text-xl font-bold text-stone-800">Agent Management</h2>
              <Input
                placeholder="Search by name, email, broker..."
                value={agentSearchQuery}
                onChange={(e) => setAgentSearchQuery(e.target.value)}
                className="max-w-sm"
              />
            </div>
          </div>
          
          {/* Mobile Card View for Agents */}
          <div className="md:hidden divide-y divide-stone-200">
            {filteredAgents.length === 0 ? (
              <div className="px-6 py-8 text-center text-stone-500">
                No agents found
              </div>
            ) : (
              paginate(filteredAgents, agentPage, agentPageSize).map((agent) => (
                <div key={agent.id} className="p-4 hover:bg-stone-50">
                  <div className="space-y-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-semibold text-stone-800">{agent.name}</p>
                        <p className="text-xs text-stone-500 mt-1">ID: {agent.id.slice(0, 8)}...</p>
                      </div>
                      {getStatusBadge(agent.status)}
                    </div>
                    
                    <div className="space-y-1 text-sm">
                      <p className="text-stone-600">{agent.email}</p>
                      {agent.phone && <p className="text-stone-500">{formatPhoneDisplay(agent.phone)}</p>}
                      <div className="flex items-center gap-1 text-stone-500">
                        <Users size={14} />
                        <span>Broker: {agent.broker_name || 'No Broker'}</span>
                      </div>
                    </div>
                    
                    <div className="flex flex-wrap gap-2 pt-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate(`/admin/agents/${agent.id}`)}
                        className="flex-1"
                      >
                        <Eye size={16} className="mr-1" />
                        View
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
          
          {/* Desktop Grid View for Agents */}
          <div className="hidden md:block overflow-x-auto">
            <div className="w-full">
              <div className="grid grid-cols-[1.5fr_2fr_1.2fr_1.2fr_1fr_2fr] bg-stone-50 border-b border-stone-200">
                <div className="px-6 py-3 text-left text-xs font-semibold text-stone-600 uppercase">Agent</div>
                <div className="px-6 py-3 text-left text-xs font-semibold text-stone-600 uppercase">Email</div>
                <div className="px-6 py-3 text-left text-xs font-semibold text-stone-600 uppercase">Phone</div>
                <div className="px-6 py-3 text-left text-xs font-semibold text-stone-600 uppercase">Broker</div>
                <div className="px-6 py-3 text-left text-xs font-semibold text-stone-600 uppercase">Status</div>
                <div className="px-6 py-3 text-right text-xs font-semibold text-stone-600 uppercase">Actions</div>
              </div>
              <div className="divide-y divide-stone-200">
                {filteredAgents.length === 0 ? (
                  <div className="px-6 py-8 text-center text-stone-500">
                    No agents found
                  </div>
                ) : (
                  paginate(filteredAgents, agentPage, agentPageSize).map((agent) => (
                    <div key={agent.id} className="grid grid-cols-[1.5fr_2fr_1.2fr_1.2fr_1fr_2fr] items-center hover:bg-stone-50">
                      <div className="px-6 py-4">
                        <p className="font-semibold text-stone-800">{agent.name}</p>
                        <p className="text-xs text-stone-500">ID: {agent.id.slice(0, 8)}...</p>
                      </div>
                      <div className="px-6 py-4 text-stone-600">{agent.email}</div>
                      <div className="px-6 py-4 text-stone-600">{agent.phone ? formatPhoneDisplay(agent.phone) : 'N/A'}</div>
                      <div className="px-6 py-4">
                        {agent.broker_name ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 rounded-full text-sm text-purple-700">
                            <Users size={14} />
                            {agent.broker_name}
                          </span>
                        ) : (
                          <span className="text-stone-400 text-sm">No Broker</span>
                        )}
                      </div>
                      <div className="px-6 py-4">{getStatusBadge(agent.status)}</div>
                      <div className="px-6 py-4">
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => navigate(`/admin/agents/${agent.id}`)}
                          >
                            <Eye size={16} className="mr-1" />
                            View
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </Card>
            {filteredAgents.length > 0 && (
              <PaginationBar filtered={filteredAgents} page={agentPage} setPage={setAgentPage} pageSize={agentPageSize} setPageSize={setAgentPageSize} label="agents" />
            )}
          </TabsContent>

          {/* Password Reset Requests Tab */}
          <TabsContent value="reset-requests" className="space-y-6">
            <Card className="bg-white overflow-hidden">
              <div className="px-4 md:px-6 py-4 border-b border-stone-200">
                <div className="flex items-center gap-3">
                  <AlertCircle className="text-[#1A4D2E]" size={24} />
                  <h2 className="text-lg md:text-xl font-bold text-stone-800">Password Reset Requests</h2>
                </div>
              </div>

              {/* Mobile Card View for Reset Requests */}
              <div className="md:hidden divide-y divide-stone-200">
                {resetRequests.length === 0 ? (
                  <div className="px-6 py-8 text-center text-stone-500">
                    No reset requests
                  </div>
                ) : (
                  resetRequests.map((request) => (
                    <div key={request.id} className="p-4 hover:bg-stone-50">
                      <div className="space-y-3">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-semibold text-stone-800">{request.user_name}</p>
                            <p className="text-xs text-stone-500 mt-1">{request.user_email}</p>
                          </div>
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                            request.status === 'pending' 
                              ? 'bg-yellow-100 text-yellow-700' 
                              : 'bg-green-100 text-green-700'
                          }`}>
                            {request.status.toUpperCase()}
                          </span>
                        </div>
                        
                        <div className="space-y-1 text-sm text-stone-600">
                          <p>Role: {request.user_role}</p>
                          <p className="text-xs text-stone-500">
                            Requested: {new Date(request.created_at).toLocaleString()}
                          </p>
                        </div>
                        
                        {request.status === 'pending' && (
                          <Button
                            size="sm"
                            onClick={() => handleResetAndFulfill(request.id, request.user_id, request.user_name, request.user_type)}
                            disabled={actionLoading === request.user_id}
                            className="w-full bg-[#1A4D2E] text-white hover:bg-[#143D24]"
                          >
                            <KeyRound size={16} className="mr-1" />
                            Reset Password & Fulfill
                          </Button>
                        )}
                        
                        {request.status === 'fulfilled' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeleteResetRequest(request.id, request.user_name)}
                            disabled={actionLoading === request.id}
                            className="w-full border-red-500 text-red-500 hover:bg-red-50"
                          >
                            <Trash2 size={16} className="mr-1" />
                            Delete Request
                          </Button>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Desktop Grid View for Reset Requests */}
              <div className="hidden md:block overflow-x-auto">
                <div className="w-full">
                  <div className="grid grid-cols-[1.5fr_2fr_1fr_1.5fr_1fr_1.5fr] bg-stone-50 border-b border-stone-200">
                    <div className="px-6 py-3 text-left text-xs font-semibold text-stone-600 uppercase">User</div>
                    <div className="px-6 py-3 text-left text-xs font-semibold text-stone-600 uppercase">Email</div>
                    <div className="px-6 py-3 text-left text-xs font-semibold text-stone-600 uppercase">Role</div>
                    <div className="px-6 py-3 text-left text-xs font-semibold text-stone-600 uppercase">Requested</div>
                    <div className="px-6 py-3 text-left text-xs font-semibold text-stone-600 uppercase">Status</div>
                    <div className="px-6 py-3 text-right text-xs font-semibold text-stone-600 uppercase">Actions</div>
                  </div>
                  <div className="divide-y divide-stone-200">
                    {resetRequests.length === 0 ? (
                      <div className="px-6 py-8 text-center text-stone-500">
                        No password reset requests
                      </div>
                    ) : (
                      resetRequests.map((request) => (
                        <div key={request.id} className="grid grid-cols-[1.5fr_2fr_1fr_1.5fr_1fr_1.5fr] items-center hover:bg-stone-50">
                          <div className="px-6 py-4">
                            <p className="font-semibold text-stone-800">{request.user_name}</p>
                          </div>
                          <div className="px-6 py-4 text-stone-600">{request.user_email}</div>
                          <div className="px-6 py-4">
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 rounded-full text-sm text-purple-700 capitalize">
                              {request.user_role}
                            </span>
                          </div>
                          <div className="px-6 py-4 text-stone-600 text-sm">
                            {new Date(request.created_at).toLocaleString()}
                          </div>
                          <div className="px-6 py-4">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${
                              request.status === 'pending' 
                                ? 'bg-yellow-100 text-yellow-700' 
                                : 'bg-green-100 text-green-700'
                            }`}>
                              {request.status === 'pending' && <Clock size={14} className="mr-1" />}
                              {request.status === 'fulfilled' && <CheckCircle size={14} className="mr-1" />}
                              {request.status.toUpperCase()}
                            </span>
                          </div>
                          <div className="px-6 py-4">
                            <div className="flex justify-end gap-2">
                              {request.status === 'pending' ? (
                                <Button
                                  size="sm"
                                  onClick={() => handleResetAndFulfill(request.id, request.user_id, request.user_name, request.user_type)}
                                  disabled={actionLoading === request.user_id}
                                  className="bg-[#1A4D2E] text-white hover:bg-[#143D24]"
                                >
                                  <KeyRound size={16} className="mr-1" />
                                  Reset & Fulfill
                                </Button>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleDeleteResetRequest(request.id, request.user_name)}
                                  disabled={actionLoading === request.id}
                                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                  title="Delete request"
                                >
                                  <Trash2 size={18} />
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </Card>
          </TabsContent>

          {/* Service Providers Tab */}
          <TabsContent value="service-providers" className="space-y-6">
            {/* Search Bar */}
            <Card className="p-4 bg-white">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" size={20} />
                <Input
                  placeholder="Search by name, company, email, phone, city, or service type..."
                  value={providerSearchQuery}
                  onChange={(e) => setProviderSearchQuery(e.target.value)}
                  className="pl-10 h-12 border-stone-300"
                />
              </div>
            </Card>

            {/* Service Providers Table */}
            <Card className="bg-white overflow-hidden">
              {/* Mobile Card View for Service Providers */}
              <div className="md:hidden divide-y divide-stone-200">
                {filteredProviders.length === 0 ? (
                  <div className="px-6 py-8 text-center text-stone-500">
                    No service providers found
                  </div>
                ) : (
                  paginate(filteredProviders, providerPage, providerPageSize).map((provider) => (
                    <div key={provider.id} className="p-4 hover:bg-stone-50">
                      <div className="space-y-3">
                        <div className="flex justify-between items-start">
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-stone-800 truncate">{provider.full_name}</p>
                            <p className="text-xs text-stone-500 mt-1 capitalize">{provider.service_type}</p>
                          </div>
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold flex-shrink-0 ${
                            provider.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-stone-200 text-stone-600'
                          }`}>
                            {provider.status === 'active' ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        
                        <div className="space-y-1 text-sm">
                          <p className="text-stone-800 font-medium">{provider.company_name || '-'}</p>
                          <p className="text-stone-600">{provider.email}</p>
                          <p className="text-stone-500">{provider.city}, {provider.state}</p>
                        </div>
                        
                        <div className="flex flex-wrap gap-2 pt-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => navigate(`/admin/providers/${provider.id}`)}
                            className="flex-1"
                          >
                            <Eye size={16} className="mr-1" />
                            View
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
              
              {/* Desktop Grid View for Service Providers */}
              <div className="hidden md:block overflow-x-auto">
                <div className="w-full">
                  <div className="grid grid-cols-[1.5fr_2fr_1.5fr_1fr_1fr_1fr] bg-stone-100">
                    <div className="px-6 py-4 text-left text-xs font-semibold text-stone-600 uppercase tracking-wider">Provider</div>
                    <div className="px-6 py-4 text-left text-xs font-semibold text-stone-600 uppercase tracking-wider">Company / Service Type</div>
                    <div className="px-6 py-4 text-left text-xs font-semibold text-stone-600 uppercase tracking-wider">Contact</div>
                    <div className="px-6 py-4 text-left text-xs font-semibold text-stone-600 uppercase tracking-wider">Location</div>
                    <div className="px-6 py-4 text-left text-xs font-semibold text-stone-600 uppercase tracking-wider">Status</div>
                    <div className="px-6 py-4 text-right text-xs font-semibold text-stone-600 uppercase tracking-wider">Actions</div>
                  </div>
                  <div className="divide-y divide-stone-200">
                    {filteredProviders.length === 0 ? (
                      <div className="px-6 py-8 text-center text-stone-500">
                        No service providers found
                      </div>
                    ) : (
                      paginate(filteredProviders, providerPage, providerPageSize).map((provider) => (
                        <div key={provider.id} className="grid grid-cols-[1.5fr_2fr_1.5fr_1fr_1fr_1fr] items-center hover:bg-stone-50">
                          <div className="px-6 py-4">
                            <p className="font-semibold text-stone-800">{provider.full_name}</p>
                          </div>
                          <div className="px-6 py-4">
                            <p className="text-stone-800">{provider.company_name || '-'}</p>
                            <p className="text-sm text-stone-500 capitalize">{provider.service_type}</p>
                          </div>
                          <div className="px-6 py-4 text-stone-600">
                            <p className="text-sm">{provider.email}</p>
                            <p className="text-sm">{formatPhoneDisplay(provider.phone)}</p>
                          </div>
                          <div className="px-6 py-4 text-stone-600">
                            <p className="text-sm">{provider.city}, {provider.state}</p>
                          </div>
                          <div className="px-6 py-4">
                            {getStatusBadge(provider.status)}
                          </div>
                          <div className="px-6 py-4">
                            <div className="flex justify-end gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => navigate(`/admin/providers/${provider.id}`)}
                              >
                                <Eye size={16} className="mr-1" />
                                View
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </Card>
            {filteredProviders.length > 0 && (
              <PaginationBar filtered={filteredProviders} page={providerPage} setPage={setProviderPage} pageSize={providerPageSize} setPageSize={setProviderPageSize} label="providers" />
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Password Reset Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#1A4D2E]">Password Reset Successful</DialogTitle>
            <DialogDescription>
              New password generated for <span className="font-semibold">{resetPasswordData?.userName}</span>
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-stone-50 p-4 rounded-lg border-2 border-[#1A4D2E]">
              <p className="text-xs text-stone-500 mb-2">New Password:</p>
              <div className="flex items-center justify-between gap-2">
                <code className="text-lg font-mono font-bold text-[#1A4D2E] break-all">
                  {resetPasswordData?.newPassword}
                </code>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={copyPasswordToClipboard}
                  className="shrink-0"
                >
                  <Copy size={16} />
                </Button>
              </div>
            </div>
            
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-sm text-yellow-800">
                <strong>⚠️ Important:</strong> Save this password securely. Share it with the user through a secure channel. This password will not be shown again.
              </p>
            </div>
            
            <Button
              onClick={() => setShowPasswordDialog(false)}
              className="w-full bg-[#1A4D2E] text-white hover:bg-[#143D24]"
            >
              Done
            </Button>
          </div>
        </DialogContent>

        {/* View Provider Details Modal */}
        <Dialog open={viewProviderModalOpen} onOpenChange={setViewProviderModalOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold text-[#1A4D2E]">Service Provider Details</DialogTitle>
              <DialogDescription className="sr-only">View provider information</DialogDescription>
            </DialogHeader>
            {selectedProvider && (
              <div className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-stone-600 text-sm">Full Name</Label>
                    <p className="text-lg font-semibold text-stone-900">{selectedProvider.full_name}</p>
                  </div>
                  <div>
                    <Label className="text-stone-600 text-sm">Service Type</Label>
                    <p className="text-lg font-semibold text-stone-900 capitalize">{selectedProvider.service_type}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-stone-600 text-sm">Email</Label>
                    <p className="text-stone-900">{selectedProvider.email}</p>
                  </div>
                  <div>
                    <Label className="text-stone-600 text-sm">Phone</Label>
                    <p className="text-stone-900">{formatPhoneDisplay(selectedProvider.phone)}</p>
                  </div>
                </div>

                {selectedProvider.company_name && (
                  <div>
                    <Label className="text-stone-600 text-sm">Company Name</Label>
                    <p className="text-stone-900">{selectedProvider.company_name}</p>
                  </div>
                )}

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-stone-600 text-sm">City</Label>
                    <p className="text-stone-900">{selectedProvider.city}</p>
                  </div>
                  <div>
                    <Label className="text-stone-600 text-sm">State</Label>
                    <p className="text-stone-900">{selectedProvider.state}</p>
                  </div>
                  <div>
                    <Label className="text-stone-600 text-sm">Zip Code</Label>
                    <p className="text-stone-900">{selectedProvider.zip_code}</p>
                  </div>
                </div>

                <div>
                  <Label className="text-stone-600 text-sm">Service Area</Label>
                  <p className="text-stone-900">{selectedProvider.service_area || 'Not specified'}</p>
                </div>

                {selectedProvider.website && (
                  <div>
                    <Label className="text-stone-600 text-sm">Website</Label>
                    <a href={selectedProvider.website} target="_blank" rel="noopener noreferrer" className="text-[#1A4D2E] hover:underline">
                      {selectedProvider.website}
                    </a>
                  </div>
                )}

                {selectedProvider.description && (
                  <div>
                    <Label className="text-stone-600 text-sm">Description</Label>
                    <p className="text-stone-900 bg-stone-50 p-3 rounded">{selectedProvider.description}</p>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-stone-600 text-sm">Agent</Label>
                    <p className="text-stone-900">{selectedProvider.agent_name || 'N/A'}</p>
                  </div>
                  <div>
                    <Label className="text-stone-600 text-sm">Status</Label>
                    <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${
                      selectedProvider.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-stone-200 text-stone-600'
                    }`}>
                      {selectedProvider.status === 'active' ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>

                <div>
                  <Label className="text-stone-600 text-sm">Registered On</Label>
                  <p className="text-stone-900">{new Date(selectedProvider.created_at).toLocaleString()}</p>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

      </Dialog>

      {/* Delete Confirmation Dialog with Passcode */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) { setDeleteTarget(null); setDeletePasscode(''); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <Trash2 size={20} />
              Delete {deleteTarget?.type === 'broker' ? 'Broker' : 'Agent'}
            </DialogTitle>
            <DialogDescription>
              This will permanently delete <strong>{deleteTarget?.name}</strong>.
              {deleteTarget?.type === 'broker' && ' Their agents will be unlinked from this brokerage.'}
              {' '}Enter your 6-digit admin passcode to confirm.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <Label className="text-sm font-medium text-stone-700">Admin Passcode</Label>
              <Input
                type="password"
                placeholder="Enter 6-digit passcode"
                value={deletePasscode}
                onChange={(e) => setDeletePasscode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                maxLength={6}
                className="mt-1 text-center text-lg tracking-widest"
                data-testid="delete-passcode-input"
                autoFocus
              />
            </div>
            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={() => { setDeleteTarget(null); setDeletePasscode(''); }}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDeleteWithPasscode}
                disabled={deleteLoading || deletePasscode.length !== 6}
                data-testid="confirm-delete-btn"
              >
                {deleteLoading ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Provider Email Dialog */}
      <Dialog open={!!editEmailProvider} onOpenChange={(open) => { if (!open) { setEditEmailProvider(null); setNewEmail(''); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#1A4D2E]">Update Provider Email</DialogTitle>
            <DialogDescription>
              Change the email address for <strong>{editEmailProvider?.full_name || editEmailProvider?.company_name}</strong>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-stone-500 block mb-1">Current Email</label>
              <p className="text-sm text-stone-800 bg-stone-50 px-3 py-2 rounded border border-stone-200">{editEmailProvider?.email}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-stone-700 block mb-1">New Email</label>
              <Input
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="Enter new email address"
                type="email"
                data-testid="edit-provider-email-input"
                autoFocus
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => { setEditEmailProvider(null); setNewEmail(''); }}>Cancel</Button>
              <Button
                onClick={handleUpdateProviderEmail}
                disabled={emailSaving || !newEmail || newEmail === editEmailProvider?.email}
                className="bg-[#1A4D2E] hover:bg-[#143D24] text-white"
                data-testid="confirm-email-update-btn"
              >
                {emailSaving ? 'Saving...' : 'Update Email'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default AdminDashboard;
